import React, {
  useState, useEffect, useCallback,
  useRef, lazy, Suspense,
}                                    from "react"
import { useNavigate }               from "react-router-dom"
import { useDispatch, useSelector }  from "react-redux"
import { selectUser }                from "../../features/userSlice"
import {
  selectActiveTabId,
  selectTabs,
  openTab,
  closeTab,
}                                    from "../../features/tabsSlice"
import {
  loadUserPrefs,
  setSidebarCollapsed,
  selectSidebarState,
}                                    from "../../features/uiSlice"
import { clearMenu }                 from "../../features/menuSlice"

import Sidebar               from "./Sidebar"
import TopBar                from "./TopBar"
import MdiTabBar             from "./MdiTabBar"
import RightPanel            from "./RightPanel"
import FloatingToolStrip     from "./FloatingToolStrip"
import StatusBar             from "./StatusBar"
import CommandPalette        from "./CommandPalette"
import DashboardContent      from "../dashboard/DashboardContent"
import { DmsPreviewProvider} from "../dms/DmsPreviewProvider"
import DmsBridge             from "../dms/DmsBridge"

// 🛡️ Per-tab error isolation — a single tab that throws (e.g. a form
//    restored from a previous build) shows a recoverable card instead of
//    blanking the whole shell. Sits one level below RootErrorBoundary.
import TabErrorBoundary      from "../TabErrorBoundary"

import "./AppShell.css"

// ── Lazy form panels — register as you build ──────────────────
const PANEL_REGISTRY = {
  dashboard: () => <DashboardContent />,
  // salesorder: lazy(() => import("../../screens/forms/sd/SalesOrder")),
  sendForApproval: lazy(() => import("../../screens/forms/le/SendForApproval")),
  linkFrghtOrder: lazy(() => import("../../screens/forms/le/LinkGrnToFreightOrder")),
  grc_menu_master:  lazy(() => import("../../screens/forms/grc/GrcMenuMaster")),
  grc_role_master:  lazy(() => import("../../screens/forms/grc/GrcRoleMaster")),
  grc_user_role:    lazy(() => import("../../screens/forms/grc/GrcUserRole")),
  GateEntryInwardLoaded:  lazy(() => import("../../screens/forms/le/gateEntryInward/GateEntryInwardLoaded")),
  GateEntryOutboundEmptyVehicle: lazy(() => import("../../screens/forms/le/outbound/GateEntryOutboundEmptyVehicle")),
  ProcurementEnquiry: lazy(() => import("../../screens/forms/mm/procurement-enquiry/ProcurementEnquiry")),
  EnquiryRegister: lazy(() => import("../../screens/forms/mm/procurement-enquiry/EnquiryRegister")),
  PoEntry: lazy(() => import("../../screens/forms/mm/po/PoEntry")),
  PoDraftWorklist: lazy(() => import("../../screens/forms/mm/po/PODraftWorklist")),
  ManualQuoteEntry: lazy(() => import("../../screens/forms/mm/procurement-enquiry/ManualQuoteEntry")),

  GstProcessMonth: lazy(() => import("../../screens/forms/fi/gst/GstProcessMonth")),

  Gstr2bUploadAndEdit: lazy(() => import("../../screens/forms/fi/gst/Gstr2bUploadAndEdit")),
  TransferErpData: lazy(() => import("../../screens/forms/fi/gst/TransferErpData")),
  GstReconcile: lazy(() => import("../../screens/forms/fi/gst/GstReconcile")),
  GstReconWorkspace: lazy(() => import("../../screens/forms/fi/gst/GstReconWorkspace")),
  ManualMatchWorkspace: lazy(() => import("../../screens/forms/fi/gst/ManualMatchWorkspace")),
  GstKpiDashboard: lazy(() => import("../../screens/forms/fi/gst/GstKpiDashboard")),
  GstErpAgeing: lazy(() => import("../../screens/forms/fi/gst/GstErpAgeing")),
  Gst2bClearance: lazy(() => import("../../screens/forms/fi/gst/Gst2bClearance")),
  GstItcSummary: lazy(() => import("../../screens/forms/fi/gst/GstItcSummary")),
  GstItcVintage: lazy(() => import("../../screens/forms/fi/gst/GstItcVintage")),
  DmsView: lazy(() => import("../../screens/forms/dms/DmsView")),
  FItb: lazy(() => import("../../screens/forms/fi/tb/TrialBalance")),
  MdmHub: lazy(() => import("../../screens/forms/mdm/MdmHubLauncher")),
}

const FormPlaceholder = ({ formType, label }) => (
  <div className="form-placeholder">
    <div className="form-placeholder-icon">🔧</div>
    <div className="form-placeholder-title">{label}</div>
    <div className="form-placeholder-sub">
      Form <code>{formType}</code> not built yet.
    </div>
  </div>
)

const FormLoader = ({ label }) => (
  <div className="form-loader">
    <div className="form-loader-spinner" />
    <span>Loading {label}…</span>
  </div>
)

// ─────────────────────────────────────────────────────────────
// AppShell inner — needs to be inside DmsPreviewProvider so
// child components (DmsBridge, forms) can use useDmsPreview().
// AppShell itself wraps this with the provider below.
// ─────────────────────────────────────────────────────────────
const AppShellInner = () => {
  const dispatch    = useDispatch()
  const navigate    = useNavigate()
  const user        = useSelector(selectUser)
  const activeTabId = useSelector(selectActiveTabId)
  const openTabs    = useSelector(selectTabs)
  const sbCollapsed = useSelector(selectSidebarState)

  const [rightPanelTab, setRightPanelTab] = useState(null)
  const [cmdOpen,       setCmdOpen]       = useState(false)

  // ── Auth guard ────────────────────────────────────────────
  useEffect(() => {
    if (!user?.token) {
      navigate("/login", { replace: true })
    }
  }, [user?.token, navigate])

  // ── On mount — prefs + dashboard tab only ─────────────────
  // Menu is fetched directly by Sidebar — not here
  const initRef = useRef(false)

  useEffect(() => {
    if (!user?.emp_id || !user?.token) return
    if (initRef.current) return
    initRef.current = true

    console.log("🚀 AppShell init for:", user.emp_id)

    // Load user preferences (theme, font, etc.)
    dispatch(loadUserPrefs())

    // Open dashboard tab if none open
    if (!openTabs || openTabs.length === 0) {
      dispatch(openTab({
        id:       "dashboard",
        formType: "dashboard",
        label:    "Dashboard",
        recordId: null,
      }))
    }
  }, [user?.emp_id, user?.token])  // eslint-disable-line

  // ── Clear menu on logout ──────────────────────────────────
  useEffect(() => {
    if (!user?.token) {
      dispatch(clearMenu())
      initRef.current = false
    }
  }, [user?.token, dispatch])

  // ── Keyboard shortcuts ────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault()
        setCmdOpen(p => !p)
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "b") {
        e.preventDefault()
        dispatch(setSidebarCollapsed(!sbCollapsed))
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault()
        document.dispatchEvent(new CustomEvent("erp:save"))
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "w") {
        e.preventDefault()
        if (activeTabId && activeTabId !== "dashboard") {
          dispatch(closeTab(activeTabId))
        }
      }
      if (e.key === "Escape") {
        if (cmdOpen)       { setCmdOpen(false);      return }
        if (rightPanelTab) { setRightPanelTab(null); return }
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [cmdOpen, rightPanelTab, activeTabId, sbCollapsed, dispatch])

  // ── Handlers ─────────────────────────────────────────────
  const handleToggleSidebar = useCallback(() => {
    dispatch(setSidebarCollapsed(!sbCollapsed))
  }, [sbCollapsed, dispatch])

  const handleRightPanel = useCallback((tab) => {
    setRightPanelTab(p => p === tab ? null : tab)
  }, [])

  // Drop I — DmsBridge calls this when dms.show() fires; we open
  // the right panel and switch to the DMS tab.
  const handleDmsShowRequested = useCallback(() => {
    setRightPanelTab("dms")
  }, [])

  // ── Render one tab's panel (called for EVERY open tab) ────
  // KEEP-MOUNTED MDI: we mount every open tab and toggle visibility
  // with CSS (display:none on inactive). React never unmounts an
  // inactive tab, so each screen KEEPS its state when you switch away
  // and back — no remount, no reset, no blank screen.
  const renderPanel = useCallback((tab) => {
    const ft    = tab.formType || tab.id
    if (ft === "dashboard") return <DashboardContent />
    const Panel = PANEL_REGISTRY[ft]
    if (!Panel) return <FormPlaceholder formType={ft} label={tab.label} />
    return (
      <Suspense fallback={<FormLoader label={tab.label} />}>
        <Panel
          tabId={tab.id}
          label={tab.label}
          recordId={tab.recordId}
          permissions={tab.permissions}
        />
      </Suspense>
    )
  }, [])

  if (!user?.token) return null

  return (
    <div className="appshell">

      {/* Drop I — invisible bridge; opens DMS tab when dms.show() is called */}
      <DmsBridge onShowRequested={handleDmsShowRequested} />

      <TopBar
        user={user}
        onToggleSidebar={handleToggleSidebar}
        onOpenPanel={handleRightPanel}
        onOpenCmd={() => setCmdOpen(true)}
        rightPanelTab={rightPanelTab}
      />

      <div className="appshell-body">

      <Sidebar
        collapsed={sbCollapsed}
        onExpand={() => dispatch(setSidebarCollapsed(false))}
      />

      <div className="appshell-main">
        <MdiTabBar />
        <div className="appshell-content">
          {/* KEEP-MOUNTED: render ALL open tabs; show only the active one.
              Each panel wrapper scrolls independently so switching tabs
              also preserves scroll position. Stable key = tab.id so React
              never recreates an instance.

              Each panel is wrapped in TabErrorBoundary so that if one tab's
              screen throws while rendering (for example a form restored from
              an older build with a payload the current code doesn't expect),
              only THAT tab shows a recoverable card — the tab bar and every
              other open tab keep working. Dashboard gets no onClose since it
              is the fallback tab and should not be closable this way. */}
          {(!openTabs || openTabs.length === 0) ? (
            <DashboardContent />
          ) : (
            openTabs.map((tab) => (
              <div
                key={tab.id}
                className="mdi-tab-panel"
                style={{ display: tab.id === activeTabId ? "block" : "none" }}
                aria-hidden={tab.id !== activeTabId}
              >
                <TabErrorBoundary
                  title={tab.label}
                  resetKey={tab.id}
                  onClose={
                    tab.id !== "dashboard"
                      ? () => dispatch(closeTab(tab.id))
                      : undefined
                  }
                >
                  {renderPanel(tab)}
                </TabErrorBoundary>
              </div>
            ))
          )}
        </div>
      </div>

      <FloatingToolStrip
        activePanel={rightPanelTab}
        onTogglePanel={handleRightPanel}
      />

      </div>

      <RightPanel
      activeTab={rightPanelTab}
      onTabChange={setRightPanelTab}
      onClose={() => setRightPanelTab(null)}
      open={!!rightPanelTab}
      />

      <StatusBar />

      {cmdOpen && (
        <CommandPalette onClose={() => setCmdOpen(false)} />
      )}

    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Outer wrapper — provides DMS preview context to the entire shell
// ─────────────────────────────────────────────────────────────
const AppShell = () => (
  <DmsPreviewProvider>
    <AppShellInner />
  </DmsPreviewProvider>
)

export default AppShell
